import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './OTPs.styles.scss';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { usePageContext } from '../../../../context/pageContext';
import moment from 'moment';
import { Button } from 'primereact/button';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

const OTPRow = React.memo(
  ({ otp, digits, className, copiedLabel, copyLabel, isRtl }) => {
    const [copied, setCopied] = useState(false);
    const [tooltipOnTop, setTooltipOnTop] = useState(false);
    const wrapperRef = useRef(null);

    return (
      <div className={cn(styles.row, className)}>
        <div className={styles.digits} aria-label="otp-digits">
          {digits?.map((digit, index) => (
            <div key={`${otp}-${index}`} className={styles.digitBox}>
              {digit}
            </div>
          ))}
        </div>
        <div className={styles.buttonWrapper} ref={wrapperRef}>
          <Button
            className={styles.copyButton}
            label={copyLabel}
            onClick={() => {
              if (wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                setTooltipOnTop(
                  isRtl
                    ? rect.left < 110
                    : window.innerWidth - rect.right < 110,
                );
              }
              navigator.clipboard.writeText(otp);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
          />
          {copied && (
            <span
              className={cn(
                styles.copiedTooltip,
                isRtl && styles.copiedTooltipRtl,
                tooltipOnTop && styles.copiedTooltipTop,
                tooltipOnTop && isRtl && styles.copiedTooltipTopRtl,
              )}
            >
              {copiedLabel}
            </span>
          )}
        </div>
      </div>
    );
  },
);

const OTPs = ({ code, copyText }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const ref = useRef();
  const exportDate = moment().format('YYYY-MM-DD-hh:mm:ss');
  const { dispatch } = usePageContext();

  useEffect(() => {
    dispatch({
      type: 'otpTableRef',
      payload: ref,
    });
  }, [dispatch]);

  const tableData = useMemo(
    () =>
      code.map((digits) => {
        const otp = digits.join('');
        return {
          otp,
          digits,
          created_at: moment().format('DD/MM/YYYY hh:mm A'),
          valid_until: moment().add(1, 'hour').format('DD/MM/YYYY hh:mm A'),
        };
      }),
    [code],
  );

  return (
    <div className={styles.container}>
      <DataTable
        exportFilename={`otp-list-${exportDate}`}
        ref={ref}
        value={tableData}
        className={styles.table}
        showHeaders={false}
      >
        <Column
          exportable
          field={'otp'}
          exportHeader={t('otp_export_header_otp')}
          body={(row) => (
            <OTPRow
              otp={row.otp}
              digits={row.digits}
              copyLabel={t('copy_code')}
              copiedLabel={t('copied')}
              isRtl={isRtl}
            />
          )}
        />
        <Column
          exportable
          field={'created_at'}
          exportHeader={t('otp_export_header_created_at')}
          hidden
        />
        <Column
          exportable
          field={'valid_until'}
          exportHeader={t('otp_export_header_valid_until')}
          hidden
        />
      </DataTable>
    </div>
  );
};

export default React.memo(OTPs);
